'use client';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef, useState } from 'react';
import benefit_bg1 from '@/app/assets/imgs/cpp/benefit_bg1.png';
import benefit_bg2 from '@/app/assets/imgs/cpp/benefit_bg2.png';
import benefit_bg3 from '@/app/assets/imgs/cpp/benefit_bg3.png';
import { BaseLineDown } from '@/app/components/icons/BaseLineDown';
import { BaseLineUp } from '@/app/components/icons/BaseLineUp';
import { Earlyaccess } from '@/app/components/icons/Earlyaccess';
import { Earncredits } from '@/app/components/icons/Earncredits';
import { Getfeatured } from '@/app/components/icons/Getfeatured';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { CustomPagination } from '@/app/components/shared/CustomPagination';
import { Empty } from '@/app/components/shared/Empty';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { Button } from '@/app/components/ui/button';
import { CPP_URL } from '@/app/constants';
import { useCreationNavigation, useFetch } from '@/app/hooks';
import { cn, reportEvent } from '@/app/utils';
import { getCreations } from '../apis';
import { PAGE_SIZE } from '../constants';
import type { CreationItem } from '../types';
import { CppCreationDetail } from './CppCreationDetail';
import { CreationCarousel } from './CreationCarousel';
import { VideoCardWrapper } from './VideoCardWrapper';

export const Cpp: React.FC = () => {
  const t = useTranslations('cpp');
  const [selectedCreation, setSelectedCreation] = useState<CreationItem | undefined>(); //详情视频
  const [page, setPage] = useState(1);
  const navigationDirection = useRef<'prev' | 'next' | null>(null);
  reportEvent('page_view', {
    cppapply: 'cpp页面',
  });
  const benefitItems = useMemo(
    () => [
      {
        title: t('ability.benefit1'),
        bg: benefit_bg1.src,
        icon: <Earncredits className="size-[82px] md:size-10" />,
      },
      {
        title: t('ability.benefit2'),
        bg: benefit_bg2.src,
        icon: <Getfeatured className="size-[82px] md:size-10" />,
      },
      {
        title: t('ability.benefit3'),
        bg: benefit_bg3.src,
        icon: <Earlyaccess className="size-[82px] md:size-10" />,
      },
    ],
    [t],
  );
  const { loading, data: creations } = useFetch(
    () => getCreations({ query: { 'pager.page': page - 1, 'pager.pagesz': PAGE_SIZE, item_type: 'creator_profile' } }),
    {
      refreshDeps: [page],
      onSuccess: ({ data }) => {
        if (data?.creatives && navigationDirection.current) {
          handlePageChange(navigationDirection.current, data.creatives);
          navigationDirection.current = null;
        }
      },
    },
  );
  const data = creations?.data;

  const handleSetSelect = (creationItem: CreationItem) => {
    setSelectedCreation(creationItem);
    reportEvent('cppvideo_click', {
      name: creationItem.title ?? '',
    });
  };

  const totalPages = useMemo(() => Math.ceil((data?.total ?? 0) / PAGE_SIZE), [data?.total]);
  // 打开详情页上下分页逻辑
  const { isPrevButtonDisabled, isNextButtonDisabled, handleSelectedCreation, handleNavigation, handlePageChange } =
    useCreationNavigation({
      items: data?.creatives,
      page,
      totalPages,
      loading,
      onPageChange: setPage,
      onCreationSelect: handleSetSelect,
    });

  // 处理前后分页
  const onNavigate = (type: 'prev' | 'next') => {
    const direction = handleNavigation(type);
    if (direction) {
      navigationDirection.current = direction;
    }
  };

  const renderCreationsList = useCallback(() => {
    if (loading) {
      return <GlobalLoader className="top-0" />;
    }

    return (
      <>
        {data?.creatives?.map((item: CreationItem) => (
          <VideoCardWrapper key={item.id} creation={item} onSelected={handleSelectedCreation} />
        ))}
        {data?.creatives?.length === 0 && <Empty emptyText={t('empty')} />}
      </>
    );
  }, [loading, data]);

  return (
    <>
      {/* 顶部轮播 */}
      <div className="relative h-screen bg-black text-center text-white md:h-[492px]">
        <div className="absolute bottom-[72px] z-10 flex w-full flex-col items-center justify-center md:bottom-10">
          <h1 className="font-semibold text-[64px] [text-shadow:_0_4px_8px_rgb(0_0_0_/_0.25)] md:text-[32px]">
            {t('slogan')}
          </h1>
          <p className="text-xl [text-shadow:_0px_4px_6px_rgb(0_0_0_/_0.25)] md:hidden">{t('description')}</p>
          <ClientLink
            href={CPP_URL}
            target="_blank"
            className="mt-8 flex h-[60px] min-w-[187px] items-center justify-center rounded-full border-[#rgba(0,0,0,.3)] bg-system-white text-black text-xl hover:bg-[linear-gradient(180deg,rgba(0,0,0,0)0%,rgba(0,0,0,0.3)100%)] md:mt-6 md:text-base"
            onClick={() => reportEvent('apply_click')}
          >
            {t('apply')}
          </ClientLink>
        </div>
        <CreationCarousel />
      </div>
      {/* 中间模块-3个图标 */}
      <div className="mx-auto mt-20 mb-40 flex max-w-[1000px] flex-col items-center md:mt-10 md:mb-14 md:px-4">
        <h2 className="text-center font-semibold text-[40px] text-white md:text-2xl">{t('ability.title')}</h2>
        <p className="text-center text-white/70 text-xl md:mt-3 md:text-xs">{t('ability.subtitle')}</p>
        <div className="mt-[60px] flex w-full justify-between gap-16 md:mt-8">
          {benefitItems.map((item) => (
            <div key={item.title} className="flex flex-col items-center">
              <div
                className="flex size-[124px] items-center justify-center rounded-full bg-center bg-cover bg-no-repeat md:size-[62px]"
                style={{ backgroundImage: `url(${item.bg})` }}
              >
                {item.icon}
              </div>
              <span className="mt-6 text-center font-medium text-[32px] md:mt-3 md:text-sm">{item.title}</span>
            </div>
          ))}
        </div>
      </div>
      {/* 底部展示作品 */}
      <div className="mx-auto mb-6 max-w-[1440px] px-[100px] md:px-4">
        <h1 className="mb-8 font-semibold text-[40px] text-white md:mb-5 md:text-2xl">{t('creators')}</h1>
        <div
          className={cn(
            'relative grid min-h-8 grid-cols-3 justify-items-center gap-6 md:grid-cols-2 md:gap-3',
            !data?.creatives?.length && 'grid-cols-1',
          )}
        >
          {renderCreationsList()}
        </div>
      </div>
      {/* 分页 */}
      <CustomPagination
        currentPage={page}
        total={data?.total ?? 0}
        pageSize={PAGE_SIZE}
        onPageChange={(page: number) => setPage(page)}
      />
      {/* 视频详情 */}
      <CppCreationDetail
        creation={selectedCreation}
        open={!!selectedCreation}
        onClose={() => setSelectedCreation(undefined)}
      >
        <div className="flex flex-col gap-2">
          <Button variant="outline" size="icon" onClick={() => onNavigate('prev')} disabled={isPrevButtonDisabled}>
            <BaseLineUp className="text-base" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => onNavigate('next')} disabled={isNextButtonDisabled}>
            <BaseLineDown className="text-base" />
          </Button>
        </div>
      </CppCreationDetail>
    </>
  );
};
